import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService } from '../../services/toast.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (message) {
      <div
        class="toast"
        [class.toast-visible]="visible"
        [class.toast-leaving]="leaving"
        role="status"
        aria-live="polite"
      >
        <span class="toast-message">{{ message }}</span>
      </div>
    }
  `,
  styles: [
    `
      .toast {
        position: fixed;
        bottom: 1.5rem;
        right: 1.5rem;
        padding: 0.75rem 1.25rem;
        background: var(--black-700, #1f1f1f);
        color: var(--black-100, #ffffff);
        font-size: 0.875rem;
        font-family: var(--font-family-primary);
        border-radius: 8px;
        box-shadow: var(--shadow-large, 0 4px 12.25px rgba(0, 0, 0, 0.25));
        z-index: 9999;
        opacity: 0;
        transform: translateY(0.75rem) scale(0.96);
        transition: opacity 0.2s ease-out, transform 0.2s ease-out;
        pointer-events: none;
      }

      .toast.toast-leaving {
        transition-duration: 0.2s;
        transition-timing-function: ease-in;
      }

      .toast.toast-visible {
        opacity: 1;
        transform: translateY(0) scale(1);
      }

      .toast-message {
        display: block;
      }
    `,
  ],
})
export class ToastComponent implements OnInit, OnDestroy {
  message: string | null = null;
  visible = false;
  leaving = false;
  private destroy$ = new Subject<void>();
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(private toast: ToastService) {}

  ngOnInit(): void {
    this.toast.toast$
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        if (this.hideTimeout) {
          clearTimeout(this.hideTimeout);
          this.hideTimeout = null;
        }
        if (msg === null) {
          this.leaving = true;
          this.visible = false;
          this.hideTimeout = setTimeout(() => {
            this.message = null;
            this.leaving = false;
            this.hideTimeout = null;
          }, 200);
          return;
        }
        this.leaving = false;
        this.message = msg;
        this.visible = false;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.visible = true;
          });
        });
        this.hideTimeout = setTimeout(() => {
          this.toast.hide();
          this.hideTimeout = null;
        }, 1200);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.hideTimeout) clearTimeout(this.hideTimeout);
  }
}
